/**
 * <ec-gift-card-recipient>
 *
 * Light-DOM Web Component for the gift-card SALE page. Owns the native
 * Shopify recipient form (see snippets/ec-gift-card-recipient.liquid) and:
 *   - enables the JS-gated "send as a gift" toggle, disables the no-JS
 *     `if_present` control, and stamps the timezone offset
 *   - reveals / hides the recipient fields, keeping them `disabled` (and thus
 *     out of the cart payload) until the toggle is on
 *   - client-side validation mirroring Shopify's limits (email required +
 *     well-formed, "Send on" within the next 90 days). Runs in the form's
 *     target phase so it pre-empts ec-product.js' document-level submit
 *     handler when invalid — no server round-trip for the common errors
 *   - live preview of the amount + recipient/sender names on the visual card
 *
 * No Shadow DOM. Idempotent define. No imports — relies only on the DOM
 * contract rendered server-side.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_SCHEDULE_DAYS = 90;

class EcGiftCardRecipient extends HTMLElement {
  connectedCallback() {
    const sid = this.dataset.sectionId;
    this.toggle = this.querySelector('[data-ec-gc-toggle]');
    this.control = this.querySelector('[data-ec-gc-control]');
    this.fields = this.querySelector('[data-ec-gc-fields]');
    this.offset = this.querySelector('[data-ec-gc-offset]');
    this.nameInput = this.querySelector('[data-ec-gc-name]');
    this.emailInput = this.querySelector('[data-ec-gc-email]');
    this.senderInput = this.querySelector('[data-ec-gc-sender]');
    this.sendonInput = this.querySelector('[data-ec-gc-sendon]');
    this.messageInput = this.querySelector('[data-ec-gc-message]');
    this.counter = this.querySelector('[data-ec-gc-counter]');
    this.liveRegion = this.querySelector('[data-ec-gc-live]');

    if (!this.toggle) return;

    // JS present: the checkbox becomes the explicit control; the no-JS
    // `if_present` hidden field steps aside so it is never double-submitted.
    this.toggle.disabled = false;
    if (this.control) this.control.disabled = true;
    if (this.offset) this.offset.value = String(new Date().getTimezoneOffset());

    this.#setScheduleBounds();

    this.form = this.closest('form');

    this.toggle.addEventListener('change', () => this.#onToggle());
    this.messageInput?.addEventListener('input', () => this.#updateCounter());
    this.nameInput?.addEventListener('input', () => this.#updatePreviewName('to', this.nameInput));
    this.senderInput?.addEventListener('input', () =>
      this.#updatePreviewName('from', this.senderInput)
    );
    [this.emailInput, this.sendonInput].forEach((el) =>
      el?.addEventListener('input', () => this.#clearFieldError(el))
    );

    // Validate before ec-product.js (document, bubble phase) processes it.
    this.form?.addEventListener('submit', (event) => this.#onSubmit(event));

    if (this.dataset.defaultOpen === 'true') {
      this.toggle.checked = true;
      this.#onToggle();
    }
  }

  #disableableFields() {
    return [
      this.nameInput,
      this.emailInput,
      this.senderInput,
      this.sendonInput,
      this.messageInput,
      this.offset,
    ];
  }

  #onToggle() {
    const on = this.toggle.checked;
    if (this.fields) this.fields.hidden = !on;
    this.#disableableFields().forEach((el) => {
      if (el) el.disabled = !on;
    });

    if (on) {
      this.#announce(this.dataset.i18nExpanded);
      // Move focus into the revealed region for keyboard/SR users.
      this.nameInput?.focus();
    } else {
      this.#clearFields();
      this.#clearAllErrors();
      this.#announce(this.dataset.i18nCollapsed);
    }
  }

  #clearFields() {
    [
      this.nameInput,
      this.emailInput,
      this.senderInput,
      this.sendonInput,
      this.messageInput,
    ].forEach((el) => {
      if (el) el.value = '';
    });
    this.#updateCounter();
    this.#updatePreviewName('to', this.nameInput);
    this.#updatePreviewName('from', this.senderInput);
  }

  // --- validation ---------------------------------------------------------

  #onSubmit(event) {
    if (!this.toggle.checked) return;

    let firstInvalid = null;
    this.#clearAllErrors();

    const email = (this.emailInput?.value || '').trim();
    if (!email) {
      this.#showFieldError('email', this.dataset.i18nErrorRequired);
      firstInvalid ||= this.emailInput;
    } else if (!EMAIL_RE.test(email)) {
      this.#showFieldError('email', this.dataset.i18nErrorEmail);
      firstInvalid ||= this.emailInput;
    }

    const sendon = (this.sendonInput?.value || '').trim();
    if (sendon && !this.#isWithinSchedule(sendon)) {
      this.#showFieldError('sendon', this.dataset.i18nErrorSendon);
      firstInvalid ||= this.sendonInput;
    }

    if (firstInvalid) {
      // Pre-empt ec-product.js' document-level submit handler.
      event.preventDefault();
      event.stopPropagation();
      firstInvalid.focus();
    }
  }

  #setScheduleBounds() {
    if (!this.sendonInput) return;
    const today = new Date();
    const max = new Date();
    max.setDate(max.getDate() + MAX_SCHEDULE_DAYS);
    this.sendonInput.min = EcGiftCardRecipient.#iso(today);
    this.sendonInput.max = EcGiftCardRecipient.#iso(max);
  }

  #isWithinSchedule(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const picked = new Date(`${value}T00:00:00`);
    if (Number.isNaN(picked.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const max = new Date(today);
    max.setDate(max.getDate() + MAX_SCHEDULE_DAYS);
    return picked >= today && picked <= max;
  }

  #showFieldError(key, message) {
    const el = this.querySelector(`[data-ec-gc-error='${key}']`);
    const input = key === 'email' ? this.emailInput : this.sendonInput;
    if (el) {
      el.textContent = message || '';
      el.hidden = false;
    }
    input?.setAttribute('aria-invalid', 'true');
  }

  #clearFieldError(input) {
    if (!input) return;
    const key = input === this.emailInput ? 'email' : input === this.sendonInput ? 'sendon' : null;
    if (!key) return;
    const el = this.querySelector(`[data-ec-gc-error='${key}']`);
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
    input.removeAttribute('aria-invalid');
  }

  #clearAllErrors() {
    this.querySelectorAll('[data-ec-gc-error]').forEach((el) => {
      el.textContent = '';
      el.hidden = true;
    });
    [this.emailInput, this.sendonInput].forEach((el) => el?.removeAttribute('aria-invalid'));
  }

  // --- live preview -------------------------------------------------------

  #updatePreviewName(slot, input) {
    const target = document.querySelector(`[data-ec-gc-${slot}]`);
    if (!target) return;
    const value = (input?.value || '').trim();
    if (value) {
      target.textContent = `${target.dataset.prefix || ''}${value}`;
      target.hidden = false;
    } else {
      target.textContent = '';
      target.hidden = true;
    }
  }

  #updateCounter() {
    if (!this.counter || !this.messageInput) return;
    const tpl = this.counter.dataset.tpl || '%n%';
    this.counter.textContent = tpl.replace('%n%', String(this.messageInput.value.length));
  }

  #announce(text) {
    if (this.liveRegion && text) this.liveRegion.textContent = text;
  }

  static #iso(date) {
    return date.toISOString().slice(0, 10);
  }
}

if (!customElements.get('ec-gift-card-recipient')) {
  customElements.define('ec-gift-card-recipient', EcGiftCardRecipient);
}

// Reflect the selected amount on the CTA + visual card. Standalone (not part
// of the recipient element) so it runs even when the recipient form is
// disabled via the section setting. ec-variant-picker.js dispatches
// `ec:variant:update` (bubbling) on every amount change.
(function () {
  if (window.__ecGiftCardAmountInit) return;
  window.__ecGiftCardAmountInit = true;

  document.addEventListener('ec:variant:update', (event) => {
    const formatted = event.detail?.variant?.price_formatted;
    if (!formatted) return;
    document.querySelectorAll('[data-ec-gc-amount], [data-ec-gc-cta-amount]').forEach((el) => {
      el.textContent = formatted;
    });
  });
})();
